import { useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { useSEO, generateServiceSchema, generateLocalBusinessSchema, generateBreadcrumbSchema } from '../../utils/seo';

const REG_TYPES = [
  {
    type: 'New Vehicle Registration',
    icon: 'ri-car-line',
    price: '$120+',
    desc: 'Register a newly purchased vehicle from a dealership or private party.',
    docs: ['Original title or MSO', 'Proof of Nevada insurance', 'Valid Nevada ID', 'Smog certificate (if required)', 'Bill of sale'],
  },
  {
    type: 'Registration Renewal',
    icon: 'ri-refresh-line',
    price: '$125+',
    desc: 'Renew your existing Nevada vehicle registration quickly — no DMV lines.',
    docs: ['Current registration card', 'Proof of Nevada insurance', 'Smog certificate (if required)', 'Valid Nevada ID'],
  },
  {
    type: 'Out-of-State Transfer',
    icon: 'ri-exchange-line',
    price: '$140+',
    desc: 'Transfer your vehicle registration from any other state to Nevada.',
    docs: ['Out-of-state title', 'Proof of Nevada insurance', 'VIN inspection (required)', 'Valid Nevada ID', 'Smog certificate'],
  },
  {
    type: 'Motorcycle Registration',
    icon: 'ri-motorbike-line',
    price: 'Call for Quote',
    desc: 'Register motorcycles, scooters, ATVs, and other specialty vehicles.',
    docs: ['Title or MSO', 'Proof of insurance', 'Valid Nevada ID', 'Safety inspection (if required)'],
  },
];

const STEPS = [
  { step: '01', icon: 'ri-phone-line', title: 'Call & Schedule', desc: 'Call 702.979.7202 to book your appointment and get a personalized document checklist.' },
  { step: '02', icon: 'ri-file-list-3-line', title: 'Gather Documents', desc: 'Bring your title, insurance proof, valid ID, and any required certificates.' },
  { step: '03', icon: 'ri-store-2-line', title: 'Visit Our Office', desc: 'Come to 4525 S Sandhill Rd. We process everything while you wait — no DMV lines.' },
  { step: '04', icon: 'ri-award-line', title: 'Drive Away', desc: 'New registration card and plates issued same day. Most completed in under 60 minutes.' },
];

const FAQS = [
  { q: 'How long does vehicle registration take?', a: 'Most registrations are completed the same day, typically within 30–60 minutes. We process all paperwork while you wait.' },
  { q: 'Do I need a smog certificate?', a: 'Smog certificates are required for most vehicles in Clark County. Vehicles newer than 4 years and certain others may be exempt. We can help determine your requirement.' },
  { q: 'Can you handle out-of-state transfers?', a: 'Yes — we specialize in out-of-state transfers from all 50 states. Title transfer, new Nevada registration, and plate issuance all in one visit.' },
  { q: 'What if I have a lien on my vehicle?', a: 'We handle vehicles with liens. Bring your lienholder information and any required lien documentation. We can add new liens or process lien releases.' },
  { q: 'Do you offer fleet registration?', a: 'Yes. We provide fleet registration for businesses with multiple vehicles, including volume pricing and business account setup.' },
];

export default function VehicleRegistrationPage() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [openType, setOpenType] = useState<number | null>(0);
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://speedydmvservices.com';

  useSEO({
    title: 'Vehicle Registration Las Vegas Nevada - Fast DMV Registration Services',
    description: 'Fast Nevada vehicle registration services in Las Vegas. New cars, renewals, out-of-state transfers. Licensed DMV provider. Same-day service available. Call 702.979.7202.',
    keywords: 'vehicle registration Las Vegas, Nevada car registration, DMV registration Las Vegas, out-of-state vehicle transfer Nevada, vehicle registration renewal',
    canonical: `${siteUrl}/vehicle-registration`,
    schema: {
      "@context": "https://schema.org",
      "@graph": [
        generateLocalBusinessSchema(),
        generateServiceSchema('Vehicle Registration Services', 'Complete vehicle registration services for new and used vehicles, renewals, and out-of-state transfers in Las Vegas Nevada.'),
        generateBreadcrumbSchema([{ name: 'Home', url: '/' }, { name: 'Services', url: '/services' }, { name: 'Vehicle Registration', url: '/vehicle-registration' }])
      ]
    }
  });

  return (
    <div className="min-h-screen bg-white font-sans">
      <Header />

      {/* HERO */}
      <section className="relative pt-[104px]">
        <div className="relative h-[400px] md:h-[460px] overflow-hidden">
          <img
            src="https://public.readdy.ai/ai/img_res/652b331e3ec99a29ed4028e1195c1db6.jpg"
            alt="Vehicle Registration Services"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-[#003366]/80"></div>
          <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-4">
            <div className="flex items-center space-x-2 text-white/60 text-xs mb-5">
              <Link to="/" className="hover:text-white transition-colors">Home</Link>
              <i className="ri-arrow-right-s-line"></i>
              <Link to="/services" className="hover:text-white transition-colors">Services</Link>
              <i className="ri-arrow-right-s-line"></i>
              <span className="text-[#F5A623]">Vehicle Registration</span>
            </div>
            <div className="inline-flex items-center space-x-2 bg-[#F5A623] text-white text-xs font-bold px-4 py-1.5 rounded mb-4 uppercase tracking-widest">
              <i className="ri-shield-check-line"></i>
              <span>Licensed 3rd Party Nevada DMV Provider</span>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-white leading-tight mb-4 max-w-3xl">
              Vehicle Registration <span className="text-[#F5A623]">Las Vegas</span>
            </h1>
            <p className="text-white/85 text-lg max-w-2xl mb-7">
              New registrations, renewals, and out-of-state transfers — all handled same day. Skip the DMV lines entirely.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <a href="tel:7029797202" className="flex items-center justify-center space-x-2 bg-[#F5A623] hover:bg-[#e09510] text-white px-7 py-3.5 font-bold text-sm transition-colors whitespace-nowrap rounded">
                <i className="fas fa-phone text-xs"></i>
                <span>Call Now: 702.979.7202</span>
              </a>
              <Link to="/appointment" className="flex items-center justify-center space-x-2 bg-white hover:bg-gray-100 text-[#003366] px-7 py-3.5 font-bold text-sm transition-colors whitespace-nowrap rounded">
                <i className="ri-calendar-check-line"></i>
                <span>Book Appointment</span>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* NOTICE BAR */}
      <div className="bg-[#FFF8E7] border-b-2 border-[#F5A623]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
          <div className="flex items-center space-x-3">
            <i className="ri-information-line text-[#F5A623] text-lg flex-shrink-0"></i>
            <p className="text-sm text-[#7a5c00]">
              <strong>We are NOT the Nevada DMV.</strong> Speedy DMV Services is a licensed, independent 3rd party provider. We assist with DMV transactions for a service fee. Official DMV: <a href="https://dmv.nv.gov" target="_blank" rel="noopener noreferrer nofollow" className="underline font-semibold">dmv.nv.gov</a>
            </p>
          </div>
        </div>
      </div>

      {/* QUICK STATS */}
      <div className="bg-[#003366]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 divide-x divide-[#1a4480]">
            {[
              { value: '$120–$150 + DMV', label: 'Service Fee' },
              { value: '30–60 Min', label: 'Avg. Processing Time' },
              { value: 'Same Day', label: 'Service Available' },
              { value: 'All 50 States', label: 'Transfers Accepted' },
            ].map((s, i) => (
              <div key={i} className="py-4 px-6 text-center">
                <p className="text-[#F5A623] text-lg font-bold">{s.value}</p>
                <p className="text-white/60 text-xs mt-0.5">{s.label}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* HOW IT WORKS */}
      <section className="py-14 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-[#003366] mb-2 border-l-4 border-[#F5A623] pl-4">Registration Process</h2>
          <p className="text-gray-500 mb-10 pl-5">Four simple steps — we handle the complexity so you don't have to.</p>
          <div className="grid md:grid-cols-4 gap-6 relative">
            <div className="hidden md:block absolute top-10 left-[12.5%] right-[12.5%] h-0.5 bg-[#003366]/15"></div>
            {STEPS.map((item, i) => (
              <div key={i} className="text-center relative group">
                <div className="w-20 h-20 mx-auto flex items-center justify-center bg-[#003366] rounded-full mb-4 relative z-10 group-hover:bg-[#F5A623] transition-colors">
                  <i className={`${item.icon} text-[#F5A623] group-hover:text-white text-2xl transition-colors`}></i>
                </div>
                <span className="text-[#F5A623] text-xs font-bold tracking-widest uppercase">Step {item.step}</span>
                <h3 className="font-bold text-[#003366] text-base mt-1 mb-2">{item.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* REGISTRATION TYPES ACCORDION */}
      <section className="py-14 bg-gray-50 border-t border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-[#003366] mb-2 border-l-4 border-[#F5A623] pl-4">Registration Types</h2>
          <p className="text-gray-500 mb-8 pl-5">Select your registration type to see required documents and pricing.</p>
          <div className="grid md:grid-cols-2 gap-5">
            {REG_TYPES.map((t, i) => (
              <div
                key={i}
                className={`border rounded-lg overflow-hidden transition-all cursor-pointer ${openType === i ? 'border-[#003366] shadow-md' : 'border-gray-200 hover:border-[#003366]/40'}`}
                onClick={() => setOpenType(openType === i ? null : i)}
              >
                <div className={`flex items-center justify-between px-5 py-4 ${openType === i ? 'bg-[#003366]' : 'bg-white hover:bg-gray-50'} transition-colors`}>
                  <div className="flex items-center space-x-3">
                    <div className={`w-10 h-10 flex items-center justify-center rounded flex-shrink-0 ${openType === i ? 'bg-[#F5A623]' : 'bg-[#003366]'}`}>
                      <i className={`${t.icon} ${openType === i ? 'text-white' : 'text-[#F5A623]'} text-base`}></i>
                    </div>
                    <div>
                      <div className="flex items-center space-x-2">
                        <p className={`font-bold text-sm ${openType === i ? 'text-white' : 'text-[#003366]'}`}>{t.type}</p>
                        <span className={`text-xs px-2 py-0.5 rounded font-bold ${openType === i ? 'bg-[#F5A623] text-white' : 'bg-[#FFF8E7] text-[#7a5c00]'}`}>{t.price}</span>
                      </div>
                      <p className={`text-xs ${openType === i ? 'text-white/70' : 'text-gray-400'}`}>{t.desc}</p>
                    </div>
                  </div>
                  <i className={`${openType === i ? 'ri-arrow-up-s-line text-white' : 'ri-arrow-down-s-line text-[#003366]'} text-xl flex-shrink-0`}></i>
                </div>
                {openType === i && (
                  <div className="px-5 py-4 bg-white border-t border-gray-100">
                    <p className="text-xs font-bold text-[#003366] uppercase tracking-widest mb-3">Required Documents</p>
                    <ul className="space-y-2">
                      {t.docs.map((doc, j) => (
                        <li key={j} className="flex items-center space-x-2 text-sm text-gray-700">
                          <i className="ri-checkbox-circle-fill text-[#F5A623] flex-shrink-0"></i>
                          <span>{doc}</span>
                        </li>
                      ))}
                    </ul>
                    <div className="mt-4 pt-4 border-t border-gray-100">
                      <a href="tel:7029797202" className="inline-flex items-center space-x-2 bg-[#003366] hover:bg-[#002244] text-white px-5 py-2.5 rounded font-bold text-sm transition-colors whitespace-nowrap">
                        <i className="fas fa-phone text-xs"></i>
                        <span>Call to Confirm Documents</span>
                      </a>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* REQUIRED DOCUMENTS */}
      <section className="py-14 bg-white border-t border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-[#003366] mb-2 border-l-4 border-[#F5A623] pl-4">Required Documents</h2>
          <p className="text-gray-500 mb-10 pl-5">Bring these documents to ensure smooth same-day processing.</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {[
              { icon: 'ri-file-text-line', title: 'Vehicle Title Documents', color: 'bg-[#003366]', items: ['Original vehicle title or MSO', 'Lien release letter (if applicable)', 'Power of attorney (if signing for another)'] },
              { icon: 'ri-shield-check-line', title: 'Insurance & Safety', color: 'bg-green-700', items: ['Proof of Nevada auto insurance', 'Smog certificate (Clark County)', 'VIN inspection (out-of-state vehicles)'] },
              { icon: 'ri-id-card-line', title: 'Personal Identification', color: 'bg-[#F5A623]', items: ['Valid government-issued photo ID', 'Social Security card or W-2', 'Proof of Nevada residency'] },
            ].map((card, i) => (
              <div key={i} className="border border-gray-200 rounded-lg overflow-hidden">
                <div className={`${card.color} px-5 py-3 flex items-center space-x-2`}>
                  <i className={`${card.icon} text-white text-base`}></i>
                  <span className="text-white font-bold text-sm">{card.title}</span>
                </div>
                <ul className="p-5 space-y-2.5">
                  {card.items.map((item, j) => (
                    <li key={j} className="flex items-start space-x-2 text-sm text-gray-700">
                      <i className="ri-checkbox-circle-fill text-[#F5A623] flex-shrink-0 mt-0.5"></i>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          <div className="mt-6 bg-[#FFF8E7] border-l-4 border-[#F5A623] rounded-r-lg p-5">
            <div className="flex items-start space-x-3">
              <i className="ri-alarm-warning-line text-[#F5A623] text-lg flex-shrink-0 mt-0.5"></i>
              <div>
                <p className="font-bold text-[#7a5c00] text-sm mb-1">Important Notes</p>
                <ul className="text-sm text-[#7a5c00] space-y-1">
                  <li>• All documents must be originals or certified copies</li>
                  <li>• Smog certificates must be completed within 90 days</li>
                  <li>• Insurance must meet Nevada minimum requirements</li>
                  <li>• Call ahead to confirm requirements for your specific situation</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FEES & FAQ */}
      <section className="py-14 bg-gray-50 border-t border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-10">
            {/* Fees */}
            <div>
              <h2 className="text-3xl font-bold text-[#003366] mb-2 border-l-4 border-[#F5A623] pl-4">Fee Schedule</h2>
              <p className="text-gray-500 mb-6 pl-5">Transparent pricing — no hidden fees.</p>
              <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                <div className="bg-[#003366] px-6 py-4">
                  <h3 className="text-white font-bold text-base flex items-center space-x-2">
                    <i className="ri-money-dollar-circle-line text-[#F5A623]"></i>
                    <span>Registration Fee Breakdown</span>
                  </h3>
                </div>
                <div className="p-6 space-y-3">
                  {[
                    { label: 'New Vehicle Registration', value: 'Call for Quote' },
                    { label: 'Registration Renewal', value: 'Call for Quote' },
                    { label: 'Out-of-State Transfer', value: 'Call for Quote' },
                    { label: 'Motorcycle Registration', value: 'Call for Quote' },
                    { label: 'Registration Reinstatement', value: 'Call for Quote' },
                    { label: 'Fleet Services', value: 'Call for Quote' },
                  ].map((row, i) => (
                    <div key={i} className={`flex justify-between items-center py-2.5 ${i < 5 ? 'border-b border-gray-100' : ''}`}>
                      <span className="text-gray-600 text-sm">{row.label}</span>
                      <span className="font-bold text-[#003366] text-sm">{row.value}</span>
                    </div>
                  ))}
                  <div className="mt-4 bg-[#FFF8E7] border border-[#F5A623]/30 rounded p-3">
                    <p className="text-xs text-[#7a5c00]"><strong>Note:</strong> Fees include our service charge plus all Nevada DMV fees. Final cost depends on vehicle type, weight, and additional services.</p>
                  </div>
                </div>
              </div>
            </div>

            {/* FAQ */}
            <div>
              <h2 className="text-3xl font-bold text-[#003366] mb-2 border-l-4 border-[#F5A623] pl-4">Common Questions</h2>
              <p className="text-gray-500 mb-6 pl-5">Quick answers to the most frequent registration questions.</p>
              <div className="space-y-3">
                {FAQS.map((faq, i) => (
                  <div
                    key={i}
                    className={`border rounded-lg overflow-hidden transition-all cursor-pointer ${openFaq === i ? 'border-[#003366]' : 'border-gray-200 hover:border-[#003366]/40'}`}
                    onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  >
                    <div className={`flex items-center justify-between px-4 py-3.5 ${openFaq === i ? 'bg-[#003366]' : 'bg-white'} transition-colors`}>
                      <p className={`font-semibold text-sm ${openFaq === i ? 'text-white' : 'text-[#003366]'}`}>{faq.q}</p>
                      <i className={`${openFaq === i ? 'ri-arrow-up-s-line text-white' : 'ri-arrow-down-s-line text-[#003366]'} text-lg flex-shrink-0 ml-3`}></i>
                    </div>
                    {openFaq === i && (
                      <div className="px-4 py-3 bg-white border-t border-gray-100">
                        <p className="text-sm text-gray-600 leading-relaxed">{faq.a}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-14 bg-[#003366]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-3">
            Ready to Register Your <span className="text-[#F5A623]">Vehicle?</span>
          </h2>
          <p className="text-white/75 mb-8 leading-relaxed">
            Skip the DMV lines and get your Nevada vehicle registration completed quickly at our Las Vegas location.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-6">
            <a href="tel:7029797202" className="flex items-center justify-center space-x-2 bg-[#F5A623] hover:bg-[#e09510] text-white px-8 py-4 font-bold text-base transition-colors whitespace-nowrap rounded">
              <i className="fas fa-phone text-sm"></i>
              <span>Call 702.979.7202</span>
            </a>
            <Link to="/required-documents" className="flex items-center justify-center space-x-2 bg-white hover:bg-gray-100 text-[#003366] px-8 py-4 font-bold text-base transition-colors whitespace-nowrap rounded">
              <i className="ri-file-list-3-line"></i>
              <span>View Document Requirements</span>
            </Link>
          </div>
          <div className="flex flex-wrap justify-center gap-5">
            {['Licensed 3rd Party Provider', 'Same-Day Processing', 'All 50 States', 'No DMV Lines'].map(b => (
              <div key={b} className="flex items-center space-x-1.5 text-white/60 text-xs">
                <i className="ri-checkbox-circle-fill text-[#F5A623]"></i>
                <span>{b}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
